import { strIsNullOrEmpty } from '@fox/atoms';
import { sendUpdate } from '@fox/extensions';
import { createContext, useContext } from 'react';
import { Relation, RepresentativeUnit } from './contracts';
import { representativeEntityId } from './util';
export type RepresentativesContextProps = {
	unit: RepresentativeUnit;
	children: React.ReactNode;
};
export type ReresentativesContext = {
	unit: RepresentativeUnit;
	getBasePosition: (position: string) => string;
	sendUpdates(input: { relationTargetId: string; relations: Relation[] }): void;
};

const Context = createContext<ReresentativesContext>(undefined);
Context.displayName = 'RepresentativesContext';
export function RepresentativesContextProvider({ children, unit }: RepresentativesContextProps) {
	/**We add the source organisation to the role string, because of this, it is hard to check whether a position has already been taken by a certain person.
	 * We need to be able to compare the actual role name (without the organisation name) to the position
	 */
	function getBasePosition(position: string) {
		if (strIsNullOrEmpty(position)) {
			return '';
		}
		if ((unit.positions?.length ?? 0) < 1) {
			return position;
		}
		const basePosition = unit.positions.find(p => position.includes(p));
		if (strIsNullOrEmpty(basePosition)) {
			return position;
		}
		return basePosition;
	}

	/**Call this after removing/adding people, it will make sure all affected tables have fresh data */
	function sendUpdates(input: { relationTargetId: string; relations: Relation[] }) {
		const { relationTargetId, relations } = input;
		if (!relationTargetId) return;
		sendUpdate(representativeEntityId({ unitId: unit.id, entityId: relationTargetId, type: 'target' }));
		const sourcesForUpdate =
			relations?.filter(m => m.relationTargetId !== relationTargetId && unit.sourceEntities.find(s => s.id === m.relationTargetId)) ??
			[];
		for (const sourceOrg of sourcesForUpdate) {
			sendUpdate(
				representativeEntityId({
					unitId: unit.id,
					entityId: sourceOrg.relationTargetId,
					type: 'source'
				})
			);
		}
	}

	const contextValue: ReresentativesContext = {
		unit,
		getBasePosition,
		sendUpdates
	};

	return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export function useRepresentativesContext() {
	const context = useContext(Context);

	if (!context) throw new Error('Missing representatives provider context');

	return context as ReresentativesContext;
}
